import { EntityTemplateField, SystemStandardField } from "../entityTemplates/EntityTemplate.types.ts";
import {
    CustomFieldValue,
    DistributionArea,
    OperationCriticalityLevelEnum,
    SystemDataType,
    SystemEntityType,
    SystemModel,
    SystemStatus,
    SystemStorageType,
} from "./systemEdit/SystemEdit.types.ts";

// The create request both asset create forms send: the pane and the dialog kept in the processing activity flow
export interface CreateSystemSaveModel {
    name: string;
    entityTemplateId: string;
    mostPopular: boolean;
    systemTypes: Array<string>;
    customFieldValues: Array<CustomFieldValue>;
    description?: string;
    distributionArea?: DistributionArea;
    entityType?: SystemEntityType;
    storageType?: SystemStorageType;
    deletionPeriodId?: string;
    deletionPeriodReason?: string;
    network?: string;
    visibleToAllUsers: boolean;
    status?: SystemStatus;
    businessAreaIds: Array<string>;
    certificationIds: Array<string>;
    dataStorageLocations: Array<string>;
    supportAccessLocations: Array<string>;
    securityMeasureIds: Array<string>;
    policyIds: Array<string>;
    additionalUserAccessIds: Array<string>;
    businessAreaAccessIds: Array<string>;
    tagIds: Array<string>;
    supplierLegalEntityIds: Array<string>;
    associatedAssetIds: Array<string>;
    dataSubjectIds: Array<string>;
    dataCategoryIds: Array<string>;
    dataTypes?: Array<SystemDataType>;
    isCriticalForOperation?: boolean;
    operationCriticalityLevel?: OperationCriticalityLevelEnum;
    usesAI?: boolean;
    companyUsesAIService?: boolean;
    aiUsageDescription?: string;
    isUseOfAIConsideredHighRisk?: boolean;
    isDataUsedForTraningOfAIModel?: boolean;
    answeredFieldIds?: Array<string>;
}

// These fields pick existing records by id. Their standard renderers edit a saved system's relations
// directly, so the create forms collect the ids themselves and send them with the create request.
export const systemCreationFieldIds: ReadonlySet<number> = new Set([
    SystemStandardField.SystemSuppliers,
    SystemStandardField.SystemAssociatedAssets,
    SystemStandardField.SystemDataSubjects,
    SystemStandardField.SystemDataCategories,
    SystemStandardField.SystemSecurityMeasures,
    SystemStandardField.SystemPolicies,
]);

export type SystemCreationFieldSelections = Partial<Record<SystemStandardField, Array<string>>>;

export function createSystemSaveModel(name: string): CreateSystemSaveModel {
    return {
        name,
        entityTemplateId: "",
        mostPopular: false,
        systemTypes: [],
        customFieldValues: [],
        visibleToAllUsers: false,
        businessAreaIds: [],
        certificationIds: [],
        dataStorageLocations: [],
        supportAccessLocations: [],
        securityMeasureIds: [],
        policyIds: [],
        additionalUserAccessIds: [],
        businessAreaAccessIds: [],
        tagIds: [],
        supplierLegalEntityIds: [],
        associatedAssetIds: [],
        dataSubjectIds: [],
        dataCategoryIds: [],
    };
}

export function getSystemFieldValue(field: EntityTemplateField, system: Partial<SystemModel>): unknown {
    if (field.customInputFieldId) {
        return system.customFieldValues?.find((v) => v.customInputFieldId === field.customInputFieldId)?.value;
    }

    switch (field.standardFieldIdentifier) {
        case SystemStandardField.SystemDescription:
            return system.description;
        case SystemStandardField.DistributionArea:
            return system.distributionArea;
        case SystemStandardField.SystemEntityType:
            return system.entityType;
        case SystemStandardField.SystemStorageType:
            return system.storageType;
        case SystemStandardField.SystemDeletionPeriod:
            return system.deletionPeriodId;
        case SystemStandardField.SystemNetwork:
            return system.network;
        case SystemStandardField.SystemBusinessAreas:
            return system.businessAreas?.length ? system.businessAreas : undefined;
        case SystemStandardField.SystemCertifications:
            return system.certifications?.length ? system.certifications : undefined;
        case SystemStandardField.SystemDataStorageLocations:
            return system.dataStorageLocations?.length ? system.dataStorageLocations : undefined;
        case SystemStandardField.SystemSupportAccessLocations:
            return system.supportAccessLocations?.length ? system.supportAccessLocations : undefined;
        case SystemStandardField.SystemSecurityMeasures:
            return system.securityMeasures?.length ? system.securityMeasures : undefined;
        case SystemStandardField.SystemPolicies:
            return system.policies?.length ? system.policies : undefined;
        case SystemStandardField.SystemDataTypes:
            return system.dataTypes?.length ? system.dataTypes : undefined;
        case SystemStandardField.SystemAdditionalUserAccess:
            return system.additionalAccessUsers?.length ? system.additionalAccessUsers : undefined;
        case SystemStandardField.SystemBusinessAreaAccess:
            return system.accessBusinessAreas?.length ? system.accessBusinessAreas : undefined;
        case SystemStandardField.SystemVisibleToAllUsers:
            return system.visibleToAllUsers;
        default:
            return undefined;
    }
}

export function mapEditedSystemToSaveFields(editedSystem: Partial<SystemModel>, selectedTagIds: string[]): Partial<CreateSystemSaveModel> {
    return {
        description: editedSystem.description,
        distributionArea: editedSystem.distributionArea,
        entityType: editedSystem.entityType,
        storageType: editedSystem.storageType,
        deletionPeriodId: editedSystem.deletionPeriodId ?? undefined,
        deletionPeriodReason: editedSystem.deletionPeriodReason,
        network: editedSystem.network,
        businessAreaIds: editedSystem.businessAreas ?? [],
        certificationIds: editedSystem.certifications ?? [],
        dataStorageLocations: editedSystem.dataStorageLocations ?? [],
        supportAccessLocations: editedSystem.supportAccessLocations ?? [],
        securityMeasureIds: editedSystem.securityMeasures?.map((m) => m.securityMeasureId) ?? [],
        policyIds: editedSystem.policies ?? [],
        additionalUserAccessIds: editedSystem.additionalAccessUsers ?? [],
        visibleToAllUsers: editedSystem.visibleToAllUsers ?? false,
        businessAreaAccessIds: editedSystem.accessBusinessAreas ?? [],
        status: editedSystem.status,
        dataTypes: editedSystem.dataTypes ?? [],
        isCriticalForOperation: editedSystem.isCriticalForOperation ?? false,
        operationCriticalityLevel: editedSystem.operationCriticalityLevel,
        usesAI: editedSystem.usesAI ?? false,
        companyUsesAIService: editedSystem.companyUsesAIService ?? false,
        aiUsageDescription: editedSystem.aiUsageDescription,
        isUseOfAIConsideredHighRisk: editedSystem.isUseOfAIConsideredHighRisk ?? false,
        isDataUsedForTraningOfAIModel: editedSystem.isDataUsedForTraningOfAIModel ?? false,
        customFieldValues: editedSystem.customFieldValues ?? [],
        tagIds: selectedTagIds,
    };
}

export function toCreateSystemRequest(model: CreateSystemSaveModel) {
    return { ...model, systemTypes: model.systemTypes.map(Number) };
}
